import { ActivityLogComment } from '@client/features/activity-log/models/activity-log-comment';
import { Comp } from '@client/features/comps/models/comp';
import {
  ConstructionMaterial,
  EditableConstructionMaterial,
} from '@client/features/material-catalog/models/construction-material';
import { DashboardProject } from '@client/features/dashboards/models/dashboard-project';
import { ProjectStage } from '@client/features/project/types';

export declare type EventType = string | symbol;
export declare type Handler<T = unknown> = (event: T) => void;
export declare type WildcardHandler<T = Record<string, unknown>> = (type: keyof T, event: T[keyof T]) => void;
export declare type EventHandlerList<T = unknown> = Array<Handler<T>>;
export declare type WildCardEventHandlerList<T = Record<string, unknown>> = Array<WildcardHandler<T>>;
export declare type EventHandlerMap<Events extends Record<EventType, unknown>> = Map<
  keyof Events | '*',
  EventHandlerList<Events[keyof Events]> | WildCardEventHandlerList<Events>
>;

export abstract class EventBus {
  abstract all: EventHandlerMap<EventsMap> | undefined;

  abstract on<Key extends keyof EventsMap>(type: Key, handler: Handler<EventsMap[Key]>): void;

  abstract on(type: '*', handler: WildcardHandler<EventsMap>): void;

  abstract off<Key extends keyof EventsMap>(type: Key, handler?: Handler<EventsMap[Key]>): void;

  abstract off(type: '*', handler: WildcardHandler<EventsMap>): void;

  abstract emit<Key extends keyof EventsMap>(type: Key, event: EventsMap[Key]): void;

  abstract emit<Key extends keyof EventsMap>(type: undefined extends EventsMap[Key] ? Key : never): void;
}

export enum Events {
  ConstructionMaterialCreated = 'ConstructionMaterialCreated',
  OpenNewMaterialModal = 'OpenNewMaterialModal',
  ConstructionMaterialDeleted = 'ConstructionMaterialDeleted',
  ObserverIntersected = 'ObserverIntersected',
  CompMapMarkerClicked = 'CompMapMarkerClicked',
  OpenCompComparisonToSubjectProperty = 'OpenCompComparisonToSubjectProperty',
  ActivityLogCommentAdded = 'ActivityLogCommentAdded',
  ActivityLogCommentDeleted = 'ActivityLogCommentDeleted',
  ActivityLogCommentUpdated = 'ActivityLogCommentUpdated',
  ProjectActivityLogUpdatePosted = 'ProjectActivityLogUpdatePosted',
  DashboardProjectStageUpdated = 'DashboardProjectStageUpdated',
}

/**
 * Map a list of Events to their event listener argument list.
 * @see client/core/composables/use-event-listeners.ts
 */
export type EventsMap = {
  [Events.ConstructionMaterialCreated]: ConstructionMaterial | EditableConstructionMaterial;
  [Events.ConstructionMaterialDeleted]: ConstructionMaterial;
  [Events.OpenNewMaterialModal]: ConstructionMaterial | undefined;
  [Events.ObserverIntersected]: Element;
  [Events.CompMapMarkerClicked]: Comp;
  [Events.OpenCompComparisonToSubjectProperty]: OpenCompComparisonToSubjectPropertyEvent;
  [Events.ActivityLogCommentAdded]: ActivityLogComment;
  [Events.ActivityLogCommentDeleted]: ActivityLogComment;
  [Events.ActivityLogCommentUpdated]: ActivityLogComment;
  [Events.ProjectActivityLogUpdatePosted]: void;
  [Events.DashboardProjectStageUpdated]: DashboardProjectStageUpdatedEvent;
};

export interface OpenCompComparisonToSubjectPropertyEvent {
  comp: Comp;
}

export interface DashboardProjectStageUpdatedEvent {
  oldStage: ProjectStage;
  newStage: ProjectStage;
  dashboardProject: DashboardProject;
}
